import React, { useState } from 'react';
import { Plus, BarChart3, History, Users, Clock, TrendingUp } from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import FinanceDashboard from '../components/finance/FinanceDashboard';
import PaymentHistory from '../components/finance/PaymentHistory';
import PatientPaymentSummary from '../components/finance/PatientPaymentSummary';
import FinanceAnalytics from '../components/finance/FinanceAnalytics';
import PaymentForm from '../components/finance/PaymentForm';
import financeService from '../services/financeService';

const Finance = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'history', label: 'Payment History', icon: History },
    { id: 'summary', label: 'Patient Summary', icon: Users },
    { id: 'analytics', label: 'Analytics & Reports', icon: TrendingUp },
    { id: 'pending', label: 'Pending Payments', icon: Clock }
  ];

  const handleAddPayment = () => {
    setSelectedPayment(null);
    setShowPaymentForm(true);
  };

  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    setShowPaymentForm(true);
  };

  const handleCloseForm = () => {
    setShowPaymentForm(false);
    setSelectedPayment(null);
  };

  const handleFormSuccess = () => {
    setShowPaymentForm(false);
    setSelectedPayment(null);
    if (activeTab === 'pending') {
      fetchPendingPayments();
    }
  };

  const handleViewPatientDetails = (patientId) => {
    window.location.href = `/patients/${patientId}`;
  };

  const fetchPendingPayments = async () => {
    try {
      setLoadingPending(true);
      const response = await financeService.getPendingPayments();
      setPendingPayments(response.data.payments);
    } catch (err) {
      console.error('Failed to fetch pending payments:', err);
    } finally {
      setLoadingPending(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  React.useEffect(() => {
    if (activeTab === 'pending') {
      fetchPendingPayments();
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      
      <div className="flex">
        <Sidebar />
        
        <div className="flex-1 p-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Finance Management</h1>
                  <p className="text-gray-600 mt-1">Track payments, revenue, and financial analytics</p>
                </div>
                <button
                  onClick={handleAddPayment}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Payment
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-md mb-6 border border-gray-200">
              <div className="flex overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center px-6 py-4 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                        activeTab === tab.id
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-5 h-5 mr-2" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="transition-all duration-200">
              {activeTab === 'dashboard' && <FinanceDashboard />}
              
              {activeTab === 'history' && (
                <PaymentHistory onEdit={handleEditPayment} />
              )}
              
              {activeTab === 'summary' && (
                <PatientPaymentSummary onViewDetails={handleViewPatientDetails} />
              )}

              {activeTab === 'analytics' && <FinanceAnalytics />}
              
              {activeTab === 'pending' && (
                <div className="space-y-4">
                  {loadingPending ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : pendingPayments.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-12 text-center border border-gray-200">
                      <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No pending payments</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Patient
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Payment Mode
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Notes
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {pendingPayments.map((payment) => (
                              <tr key={payment.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">
                                    {payment.patient_name}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-semibold text-orange-700">
                                    {formatCurrency(payment.amount)}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">{payment.payment_mode}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-600">{formatDate(payment.payment_date)}</div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm text-gray-500 max-w-xs truncate">
                                    {payment.notes || '-'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={() => handleEditPayment(payment)}
                                    className="text-indigo-600 hover:text-indigo-900 font-medium"
                                  >
                                    Mark as Paid
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Payment Form Modal */}
            {showPaymentForm && (
              <PaymentForm
                payment={selectedPayment}
                onClose={handleCloseForm}
                onSuccess={handleFormSuccess}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Finance;